import React, { useEffect, useState } from 'react';
import { Input, Table, Typography, message } from 'antd';
import { useLocation } from 'react-router-dom';
import { getReportApiByKey } from '@app/api/reports.api';

const { Title } = Typography;

interface FlatRow {
  key: string;
  department: string;
  category: string;
  mainTest: string;
  subTest: string;
  qty: number;
  uom: string;
}

interface LabFlatReportTableProps {
  reportKey: string;
  filters: any;
}

const SearchFilter: React.FC<{ onSearch: (keyword: string) => void }> = ({ onSearch }) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearch(e.target.value);
  };

  const handleSearch = (value: string) => {
    onSearch(value);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Input.Search
        placeholder="Search by department, category, or test..."
        allowClear
        onChange={handleInputChange}
        onSearch={handleSearch}
        enterButton
        style={{ width: 300 }}
      />
    </div>
  );
};

export const LabFlatReportTable: React.FC<LabFlatReportTableProps> = ({ reportKey, filters }) => {
  const location = useLocation();
  const [tableData, setTableData] = useState<FlatRow[]>([]);
  const [filteredData, setFilteredData] = useState<FlatRow[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!filters || !reportKey) return;

      const reportApi = getReportApiByKey(reportKey);
      if (!reportApi) {
        message.error('Invalid report key.');
        return;
      }

      try {
        const response = await reportApi.generateReport(filters);
        const rows: FlatRow[] = (response?.report || []).map((item: any, index: number) => ({
          key: `${index}`,
          department: item.department || '',
          category: item.category || '',
          mainTest: item.sample || '',
          subTest: item.service || '',
          qty: parseFloat(item.total_amount) || 0,
          uom: item.uom || '',
        }));

        setTableData(rows);
        setFilteredData(rows);
      } catch (err) {
        message.error('Failed to fetch report data.');
        console.error(err);
      }
    };

    fetchData();
  }, [filters, reportKey]);

  const handleSearch = (keyword: string) => {
    if (!keyword || keyword.trim() === '') {
      setFilteredData(tableData);
      return;
    }

    const lower = keyword.toLowerCase();

    const filtered = tableData.filter(
      (item) =>
        item.department.toLowerCase().includes(lower) ||
        item.category.toLowerCase().includes(lower) ||
        item.mainTest.toLowerCase().includes(lower) ||
        item.subTest.toLowerCase().includes(lower),
    );

    setFilteredData(filtered);
  };

  const columns = [
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'MainTest Level',
      dataIndex: 'mainTest',
      key: 'mainTest',
    },
    {
      title: 'SubTest Level',
      dataIndex: 'subTest',
      key: 'subTest',
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      key: 'qty',
      render: (value: number) => value.toFixed(2),
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      render: (text: any) => text || '-',
    },
  ];

  return (
    <div>
      <SearchFilter onSearch={handleSearch} />
      <Table columns={columns} dataSource={filteredData} rowKey="key" pagination={false} />
    </div>
  );
};
